import { lazy, Suspense, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Navigate as NavigateRoute, Route, Routes, useLocation } from 'react-router-dom';
import AudioPlayer from '../components/audioPlayer/Player.v2';
import Navigate from '../components/Navigate';
import { MusicPlayerStateContext } from '../contexts/MusicContext';
import CouponPage from '../pages/Promotion/CouponPage';
import Header from '../components/Header';
import { MobileSiteHeader } from '../components/layout/MobileSiteHeader';
import { MobileNavDrawer } from '../components/layout/MobileNavDrawer';
import { AmbientBackground } from '../components/layout/AmbientBackground';
import Footer from '../components/Footer';
import { SearchOverlay } from '../components/search/SearchOverlay';
import { DraftAppToast } from '../components/common/DraftAppToast';
import { setAppToastListener, type AppToastContent } from '../utils/appToast';
import { scrollAppShellPageToTop } from '../hooks/useAppPageScrollToTop';
import { AppRouteSuspenseFallback } from './AppRouteSuspenseFallback';
import '../components/layout/app-shell.css';

const HomeRoot = lazy(() => import('../pages/Home/Root'));
const SearchRoot = lazy(() => import('../pages/Search/Root'));
const PlayerPage = lazy(() => import('../pages/Player/index'));
const ProfilePage = lazy(() => import('../pages/Profile/Root'));
const CategoryRoot = lazy(() => import('../pages/Category/Root'));
const PrivatePolicyPage = lazy(() => import('../pages/Consent/PrivatePolicyPage'));
const SettingRoot = lazy(() => import('../pages/Setting/Root'));
const TermsOfUsePage = lazy(() => import('../pages/Consent/TermsOfUsePage'));
const MarketingInformationPage = lazy(() => import('../pages/Consent/MarketingInforamtionPage'));

const PopularClassPage = lazy(() => import('../pages/Class/Popular'));
const RecentClassPage = lazy(() => import('../pages/Class/Recent'));

const MembershipPage = lazy(() => import('../pages/Consent/MembershipPage'));
const GiftRoot = lazy(() => import('../pages/Gift/Root'));
const PlaylistRoot = lazy(() => import('../pages/Playlist/Root'));
const NoticeRoot = lazy(() => import('../pages/Notice/Root'));
const AnalysisRoot = lazy(() => import('../pages/Analysis/Root'));
const PaymentResult = lazy(() => import('../pages/PaymentResult'));

export default function AppRoute() {
  const { currentPlayMusic } = useContext(MusicPlayerStateContext);
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [appToast, setAppToast] = useState<{ id: number; content: AppToastContent } | null>(null);
  const appToastIdRef = useRef(0);

  useEffect(() => {
    setAppToastListener((content) => {
      appToastIdRef.current += 1;
      setAppToast({ id: appToastIdRef.current, content });
    });
    return () => setAppToastListener(null);
  }, []);

  useEffect(() => {
    if (!appToast) return;
    const timer = window.setTimeout(() => setAppToast(null), 2600);
    return () => window.clearTimeout(timer);
  }, [appToast]);

  /** 라우트·뒤로가기 시 앱 스크롤 초기화 (상세 → 목록 등 pathname 동일 재진입 포함) */
  useLayoutEffect(() => {
    scrollAppShellPageToTop();
  }, [location.pathname, location.search, location.key]);

  const noHeader = location.pathname.startsWith('/app/membership');
  const isPlayerPage = location.pathname.includes('/app/player/');
  const isAnalysisResultPage = location.pathname.startsWith('/app/analysis/result');
  const isHomeRecommendedPage = location.pathname.startsWith('/app/home/recommended');
  const isImmersivePage = isPlayerPage || isAnalysisResultPage;
  const showSiteFooter = !isImmersivePage && !isHomeRecommendedPage;

  const shellModifierClass = [
    isAnalysisResultPage && 'soundpill-app-shell--analysis-result-route',
    isPlayerPage && 'soundpill-app-shell--player-route',
    noHeader && 'soundpill-app-shell--no-mobile-header',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={`soundpill-app-shell relative w-full${shellModifierClass ? ` ${shellModifierClass}` : ''}`}
    >
      <AmbientBackground />
      {!isAnalysisResultPage ? <Header /> : null}
      {!isAnalysisResultPage ? <Navigate /> : null}

      {!noHeader && !isImmersivePage ? (
        <MobileSiteHeader onMenuOpen={() => setMenuOpen(true)} />
      ) : null}

      <div className="soundpill-app-shell__body">
        <div
          className={`soundpill-app-shell__main${isPlayerPage ? ' soundpill-app-shell__main--player' : ''}`}
        >
          <div className="soundpill-app-shell__scroll">
            <div
              className={`soundpill-app-shell__page no-scrollbar ${
                isPlayerPage
                  ? 'soundpill-app-shell__page--player h-full'
                  : isAnalysisResultPage
                    ? 'soundpill-app-shell__page--analysis-result h-full'
                    : noHeader
                      ? 'h-full'
                      : 'h-[calc(100dvh-var(--mobile-site-header-height))] lg:h-full'
              }`}
            >
              <div
                className={`soundpill-app-shell__page-inner${
                  !isImmersivePage && currentPlayMusic ? ' soundpill-app-shell__page-inner--with-mini-player' : ''
                }${isPlayerPage ? ' soundpill-app-shell__page-inner--player' : ''}${
                  isAnalysisResultPage ? ' soundpill-app-shell__page-inner--analysis-result' : ''
                }`}
              >
                <div className="soundpill-app-shell__page-main">
                  <Suspense fallback={<AppRouteSuspenseFallback />}>
                    <Routes>
                      <Route path="home/*" element={<HomeRoot />} />
                      <Route path="playlists/*" element={<PlaylistRoot />} />
                      <Route path="search/*" element={<SearchRoot />} />
                      <Route path="category/:id/*" element={<CategoryRoot />} />
                      <Route path="profile/*" element={<ProfilePage />} />
                      <Route path="class/recent-list" element={<RecentClassPage />} />
                      <Route path="class/popular-list" element={<PopularClassPage />} />
                      <Route path="player/:id" element={<PlayerPage />} />
                      <Route path="setting/*" element={<SettingRoot />} />
                      <Route path="notice/*" element={<NoticeRoot />} />
                      <Route path="analysis/*" element={<AnalysisRoot />} />
                      <Route path="terms-of-use" element={<TermsOfUsePage />} />
                      <Route path="privacy-policy" element={<PrivatePolicyPage />} />
                      <Route path="marketing-information" element={<MarketingInformationPage />} />
                      <Route path="gift/*" element={<GiftRoot />} />
                      <Route path="membership" element={<MembershipPage />} />
                      <Route path="payment/result" element={<PaymentResult />} />
                      <Route path="promotion/coupon" element={<CouponPage />} />
                      <Route path="*" element={<NavigateRoute to="home" replace />} />
                    </Routes>
                  </Suspense>
                </div>
                {showSiteFooter && <Footer />}
              </div>
            </div>
          </div>
          <AudioPlayer />
        </div>
      </div>

      <MobileNavDrawer open={menuOpen} onClose={() => setMenuOpen(false)} />

      <SearchOverlay />
      {appToast ? <DraftAppToast key={appToast.id} message={appToast.content} /> : null}
    </div>
  );
}
